<template>
  <a-input
    v-model:value="displayValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :maxlength="maxLength"
    :status="status"
    class="ant-coupon-code-input"
    @update:value="onInput"
    @blur="onBlur"
    @pressEnter="onPressEnter"
  >
    <template #prefix>
      <i class="fas fa-ticket-alt" />
    </template>
    <template v-if="showApply" #suffix>
      <h-button type="link" size="small" :disabled="!displayValue || disabled" @click="onApply">
        {{ applyText }}
      </h-button>
    </template>
  </a-input>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  status?: "" | "error" | "warning";
  maxLength?: number;
  uppercase?: boolean;
  showApply?: boolean;
  applyText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  placeholder: "Nhập mã giảm giá",
  disabled: false,
  size: "middle",
  allowClear: true,
  status: "",
  maxLength: 30,
  uppercase: true,
  showApply: true,
  applyText: "Áp dụng",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "change", value: string): void;
  (e: "apply", value: string): void;
  (e: "blur", event: FocusEvent): void;
  (e: "pressEnter", event: KeyboardEvent): void;
}>();

const displayValue = ref("");

const normalize = (value: string) => {
  let next = value.replace(/[^a-zA-Z0-9\-_]/g, "");
  if (props.uppercase) next = next.toUpperCase();
  return next.slice(0, props.maxLength);
};

watch(
  () => props.modelValue,
  (val) => {
    displayValue.value = normalize(val ?? "");
  },
  { immediate: true }
);

const onInput = (val: string) => {
  const next = normalize(val);
  displayValue.value = next;
  emit("update:modelValue", next);
  emit("change", next);
};

const onApply = () => {
  if (!displayValue.value) return;
  emit("apply", displayValue.value);
};

const onBlur = (e: FocusEvent) => emit("blur", e);

const onPressEnter = (e: KeyboardEvent) => {
  emit("pressEnter", e);
  onApply();
};
</script>

<!--
  Hướng dẫn sử dụng — Coupon Code
  Global: <h-coupon v-model="code" @apply="applyCoupon" />
  Import: import { CouponCodeInput } from '@/components/ant/input'

  Props: modelValue, showApply, applyText, uppercase, maxLength...
  Events: update:modelValue, change, apply, blur, pressEnter
  Docs: src/components/ant/input/README.md
-->
